// src/components/video/clockScene.js
//
// An analog teaching clock drawn on a canvas. Always correct: the hands are
// placed from the time you give it, never drawn by hand.
//
//   drawClock(ctx, {
//     cx, cy, r,
//     hour, minute,              // 3, 0 → 3:00. Fractions allowed (the hour
//                                // hand drifts with the minutes, like a real clock)
//     glowHour, glowMinute,      // 0..1 — halo on that hand
//     glowNumbers: [1, 2],       // numbers to light up (0..1 strength via glowNumberAmt)
//     glowNumberAmt,             // 0..1
//     glowNotches: [1, 2, 3],    // minute notches (1-60) to light
//     ringHours, ringMinutes,    // 0..1 — pulse the ring of numbers / notches
//     fiveLabels,                // 0..12 — how many "5, 10, 15…" labels are showing
//     wobbleHour,                // 0..1 — a little wiggle on the hour hand
//     t,                         // seconds (for pulses)
//   })
//
// Colours: the HOUR hand is short, thick and orange; the MINUTE hand is long,
// thinner and blue. The video's labels use the same colours, so children can
// tie "little hand = hour" to a colour as well as a size.

export const HOUR_COLOR = "#f08a24";
export const MINUTE_COLOR = "#2a6ff0";
const INK = "#2a2130";
const TAU = Math.PI * 2;

const angleFor = (fractionOfTurn) => fractionOfTurn * TAU - Math.PI / 2;

const hand = (ctx, cx, cy, ang, len, width, color, glow) => {
  const x = cx + Math.cos(ang) * len;
  const y = cy + Math.sin(ang) * len;
  if (glow > 0) {
    ctx.save();
    ctx.globalAlpha = 0.35 * glow;
    ctx.strokeStyle = color;
    ctx.lineWidth = width + 22 * glow;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.restore();
  }
  // tail + outline + fill
  const tail = len * 0.16;
  const tx = cx - Math.cos(ang) * tail;
  const ty = cy - Math.sin(ang) * tail;
  ctx.lineCap = "round";
  ctx.strokeStyle = INK;
  ctx.lineWidth = width + 5;
  ctx.beginPath();
  ctx.moveTo(tx, ty);
  ctx.lineTo(x, y);
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(tx, ty);
  ctx.lineTo(x, y);
  ctx.stroke();
};

export const drawClock = (ctx, o) => {
  const {
    cx,
    cy,
    r,
    hour = 12,
    minute = 0,
    glowHour = 0,
    glowMinute = 0,
    glowNumbers = [],
    glowNumberAmt = 1,
    glowNotches = [],
    ringHours = 0,
    ringMinutes = 0,
    fiveLabels = 0,
    wobbleHour = 0,
    t = 0,
  } = o;

  ctx.save();
  ctx.lineJoin = "round";

  // shadow
  ctx.beginPath();
  ctx.arc(cx + 6, cy + 10, r * 1.08, 0, TAU);
  ctx.fillStyle = "rgba(30,20,40,0.16)";
  ctx.fill();

  // rim
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.08, 0, TAU);
  const rim = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
  rim.addColorStop(0, "#7a5cf0");
  rim.addColorStop(1, "#5a3fd0");
  ctx.fillStyle = rim;
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = INK;
  ctx.stroke();

  // face
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TAU);
  ctx.fillStyle = "#fffdf6";
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.stroke();

  // minute ring pulse (the notches)
  if (ringMinutes > 0) {
    ctx.save();
    ctx.globalAlpha = 0.25 + 0.2 * Math.sin(t * 6);
    ctx.globalAlpha *= ringMinutes;
    ctx.strokeStyle = MINUTE_COLOR;
    ctx.lineWidth = r * 0.12;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.93, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }
  // hour ring pulse (the numbers)
  if (ringHours > 0) {
    ctx.save();
    ctx.globalAlpha = (0.22 + 0.18 * Math.sin(t * 6)) * ringHours;
    ctx.strokeStyle = HOUR_COLOR;
    ctx.lineWidth = r * 0.2;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.74, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }

  // notches: 60, every fifth longer
  const lit = new Set(glowNotches);
  for (let n = 0; n < 60; n += 1) {
    const a = angleFor(n / 60);
    const major = n % 5 === 0;
    const r0 = r * (major ? 0.84 : 0.89);
    const r1 = r * 0.96;
    const on = lit.has(n === 0 ? 60 : n);
    if (on) {
      ctx.beginPath();
      ctx.arc(cx + Math.cos(a) * r * 0.925, cy + Math.sin(a) * r * 0.925, r * 0.05, 0, TAU);
      ctx.fillStyle = "rgba(42,111,240,0.3)";
      ctx.fill();
    }
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
    ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
    ctx.strokeStyle = on ? MINUTE_COLOR : INK;
    ctx.lineWidth = on ? 5 : major ? 4 : 2;
    ctx.lineCap = "round";
    ctx.stroke();
  }

  // numbers
  const glow = new Set(glowNumbers);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let n = 1; n <= 12; n += 1) {
    const a = angleFor(n / 12);
    const x = cx + Math.cos(a) * r * 0.71;
    const y = cy + Math.sin(a) * r * 0.71;
    if (glow.has(n) && glowNumberAmt > 0) {
      const pulse = 1 + 0.06 * Math.sin(t * 8);
      ctx.beginPath();
      ctx.arc(x, y, r * 0.13 * pulse, 0, TAU);
      ctx.fillStyle = `rgba(240,138,36,${0.85 * glowNumberAmt})`;
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = INK;
      ctx.stroke();
    }
    ctx.font = `800 ${Math.round(r * 0.17)}px "Baloo 2", "Fredoka", "Trebuchet MS", sans-serif`;
    ctx.fillStyle = glow.has(n) && glowNumberAmt > 0.5 ? "#ffffff" : INK;
    ctx.fillText(String(n), x, y + r * 0.012);
  }

  // "5, 10, 15 …" minute labels outside the rim
  const shown = Math.max(0, Math.min(12, fiveLabels));
  for (let n = 1; n <= Math.ceil(shown); n += 1) {
    const k = Math.min(1, shown - (n - 1));
    const a = angleFor(n / 12);
    const rr = r * 1.24;
    const x = cx + Math.cos(a) * rr;
    const y = cy + Math.sin(a) * rr;
    const s = 0.6 + 0.4 * k;
    ctx.save();
    ctx.globalAlpha = k;
    ctx.translate(x, y);
    ctx.scale(s, s);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.12, 0, TAU);
    ctx.fillStyle = MINUTE_COLOR;
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = INK;
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.font = `800 ${Math.round(r * 0.12)}px "Baloo 2", "Fredoka", "Trebuchet MS", sans-serif`;
    ctx.fillText(String((n * 5) % 60 === 0 ? 60 : n * 5), 0, 1);
    ctx.restore();
  }

  // hands
  const h = ((hour % 12) + minute / 60) / 12;
  const wob = wobbleHour > 0 ? Math.sin(t * 10) * 0.06 * wobbleHour : 0;
  hand(ctx, cx, cy, angleFor(h) + wob, r * 0.5, r * 0.07, HOUR_COLOR, glowHour);
  hand(ctx, cx, cy, angleFor(minute / 60), r * 0.8, r * 0.04, MINUTE_COLOR, glowMinute);

  // centre cap
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.06, 0, TAU);
  ctx.fillStyle = INK;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.025, 0, TAU);
  ctx.fillStyle = "#ffd23f";
  ctx.fill();

  ctx.restore();
};

/** Where a clock number sits on screen, e.g. to aim an arrow at it. */
export const clockNumberPos = (cx, cy, r, n, radius = 0.71) => {
  const a = angleFor(n / 12);
  return { x: cx + Math.cos(a) * r * radius, y: cy + Math.sin(a) * r * radius };
};

export default drawClock;
